import React, { useRef } from "react";
import LoadingScreen from "../LoadingScreen";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useAnalyticsSummary } from "../../hooks/useAnalyticsSummary";
import { useAutopilotEnabled } from "../../hooks/useAutopilotEnabled";
import WindowTabs from "../../components/analytics/WindowTabs";
import StatTiles from "../../components/analytics/StatTiles";
import PostsPerDayChart from "../../components/analytics/PostsPerDayChart";
import BreakdownList from "../../components/analytics/BreakdownList";
import RecentPostsList from "../../components/analytics/RecentPostsList";
import AnalyticsEmptyState from "../../components/analytics/AnalyticsEmptyState";
import AnalyticsErrorState from "../../components/analytics/AnalyticsErrorState";
import { formatRange, platformLabel, postTypeLabel } from "../../components/analytics/labels";
import { cn } from "../../utils/classMerger";

const Skeleton: React.FC = () => (
    <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 min-[900px]:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-24 rounded-lg bg-gray-200 animate-skeleton" />
            ))}
        </div>
        <div className="h-64 rounded-lg bg-gray-200 animate-skeleton" />
    </div>
);

const AnalyticsScreen: React.FC = () => {
    const mobile = useIsMobile(900);
    const { data, loading, error, window, setWindow, refresh } = useAnalyticsSummary(7);
    const { autopilotEnabled, loading: autopilotLoading } = useAutopilotEnabled();
    const hasLoadedOnce = useRef(false);
    if (data) hasLoadedOnce.current = true;

    if (loading && !hasLoadedOnce.current) {
        return <LoadingScreen loadingMessage="Loading your analytics" />;
    }

    const rangeLabel = data ? formatRange(data.start, data.end) : null;
    const empty = !!data && data.totals.published === 0;

    return (
        <div className={cn("mx-auto w-full max-w-6xl", mobile ? "p-4" : "p-6")}>
            <div className="mb-4 flex flex-col gap-3 min-[900px]:flex-row min-[900px]:items-end min-[900px]:justify-between">
                <div>
                    <h1 className={cn("font-bold tracking-tight text-primary", mobile ? "text-2xl" : "text-3xl")}>
                        Analytics
                    </h1>
                    <p className="text-gray-500">What Marabot has published for you.</p>
                </div>
                <WindowTabs value={window} onChange={setWindow} rangeLabel={rangeLabel} />
            </div>

            {loading ? (
                <Skeleton />
            ) : error ? (
                <AnalyticsErrorState message={error} onRetry={refresh} />
            ) : !data ? (
                <Skeleton />
            ) : empty ? (
                <AnalyticsEmptyState
                    window={window}
                    autopilotEnabled={autopilotEnabled}
                    ctaReady={!autopilotLoading}
                />
            ) : (
                <div className="space-y-4">
                    <StatTiles totals={data.totals} />
                    <PostsPerDayChart perDay={data.perDay} />
                    <div className="grid grid-cols-1 gap-4 min-[900px]:grid-cols-2">
                        <BreakdownList
                            title="By network"
                            rows={data.byPlatform}
                            labelOf={platformLabel}
                            showPlatformIcon
                            emptyText="No networks in this window."
                        />
                        <BreakdownList
                            title="By content type"
                            rows={data.byPostType}
                            labelOf={postTypeLabel}
                            emptyText="No content in this window."
                        />
                    </div>
                    <RecentPostsList posts={data.recent} timeZone={data.timeZone} />
                </div>
            )}
        </div>
    );
};

export default AnalyticsScreen;
